import {
  Component,
  EventEmitter,
  Input,
  Output,
  LOCALE_ID,
  ViewChild,
  ElementRef,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { FormGroup } from '@angular/forms';
import { BalanceService } from 'src/app/services/api/balance.service';
import { ToastService } from 'src/app/services/toast.service';
import * as moment from 'moment';
import 'moment/locale/id';
import {
  LocaleConfig,
  DaterangepickerDirective,
} from 'ngx-daterangepicker-material';
import { NgbDateParserFormatter, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Dayjs } from 'dayjs';
import { HistoryBalanceModel } from 'src/app/models/history-balance.model';
import { BalanceResponseType } from 'src/app/types/response/balance-response.type';
import { ResponseCustomType } from 'src/app/types/response/response.type';
import { balanceModel } from 'src/app/models/balance.model';
import { ModalDlRincianSaldo } from 'src/app/modules/shared/components/modal-download-rincian-saldo/modal-download-rincian-saldo.component';
import { LocaleConfigDate } from 'src/app/constants/date-config.const';
import {
  BalanceStatusOption,
  DateRangeSelection,
  FilterHistoryBalance,
} from 'src/app/models/balance-history.model';
import {
  BALANCE_DATE_RANGES,
  BALANCE_STATUS_OPTIONS,
  DEFAULT_TRANSACTION_TYPES,
}
from 'src/app/constants/detail-saldo.const';
import { DashboardRanges, DateRangeChangeEvent, RangesDate } from 'src/app/models/dashboard.model';

@Component({
  standalone: false,
  selector: 'app-detail-saldo',
  templateUrl: './detail-saldo.component.html',
  styleUrls: ['./detail-saldo.component.scss'],
  providers: [{ provide: LOCALE_ID, useValue: 'id' }],
})
export class DetailSaldoComponent implements OnInit, OnDestroy {
  @Input() HistoryList: HistoryBalanceModel[] = [];
  @Input() filterHistory: FilterHistoryBalance = {
    start_date: moment().format('YYYY-MM-DD'),
    end_date: moment().format('YYYY-MM-DD'),
    transaction_type: DEFAULT_TRANSACTION_TYPES,
    limit: 50,
    offset: 0,
    airway_bill: '',
  };
  keepCalendarOpeningWithRange: boolean;
  @Output() filterResult = new EventEmitter<FilterHistoryBalance>();
  destroy$ = new Subject<void>();
  @ViewChild(DaterangepickerDirective, { static: false })
  pickerDirective!: DaterangepickerDirective;
  @ViewChild('checkBoxRef', { static: false }) checkBoxRef!: ElementRef;
  @Input() balance: balanceModel = {};
  form!: FormGroup;
  uncheck = false;
  uncheckList!: boolean;
  private searchSubject = new Subject<string>();

  selected: DateRangeSelection = {
    startDate: moment(),
    endDate: moment(),
  };

  ranges: any = BALANCE_DATE_RANGES;

  invalidDates: moment.Moment[] = [
    moment().add(2, 'days'),
    moment().add(3, 'days'),
    moment().add(5, 'days'),
  ];

  isInvalidDate = (m: moment.Moment) => {
    return this.invalidDates.some((d) => d.isSame(m, 'day'));
  };

  setlocale: LocaleConfig = LocaleConfigDate;

  status: BalanceStatusOption[] = [...BALANCE_STATUS_OPTIONS];

  isSemua: boolean = false;
  selectedStatuses: string[] = [];
  resi: string = '';
  loading: boolean = true;

  constructor(
    private balanceService: BalanceService,
    private route: Router,
    private toastService: ToastService,
    public formatter: NgbDateParserFormatter,
    private modalService: NgbModal
  ) {
    this.keepCalendarOpeningWithRange = true;
  }

  ngOnInit(): void {
    this.getBalance();
    this.getHistory();

    this.searchSubject
      .pipe(debounceTime(1000), takeUntil(this.destroy$))
      .subscribe((value) => {
        this.filterHistory.airway_bill = value;
        this.filterHistory.offset = 0;
        this.filterResult.emit(this.filterHistory);
        this.getHistory();
      });

    setTimeout(() => {
      this.loading = false;
    }, 1000);
  }

  getBalance() {
    this.balanceService
      .balance()
      .pipe(takeUntil(this.destroy$))
      .subscribe((res: ResponseCustomType<BalanceResponseType>) => {
        this.balance = res.data;
      });
  }

  back() {
    this.route.navigate(['finance/balance']);
  }

  onChangeSearch(value: string) {
    if (value.length >= 3 || value.length === 0) {
      this.searchSubject.next(value);
    }
  }

  getStatus(event: Event, list: BalanceStatusOption) {
    const inputElement = event.target as HTMLInputElement;
    const isChecked = inputElement.checked;

    if (isChecked) {
      if (!this.selectedStatuses.includes(list.value)) {
        this.selectedStatuses.push(list.value);
      }
    } else {
      this.selectedStatuses = this.selectedStatuses.filter(
        (value) => value !== list.value
      );
    }

    if (this.selectedStatuses.length > 0) {
      this.filterHistory.transaction_type = this.selectedStatuses.join(',');
    } else {
      this.filterHistory.transaction_type = DEFAULT_TRANSACTION_TYPES;
      this.selectedStatuses = [];
    }

    this.status = this.status.map((item) => {
      if (item.value === list.value) {
        return { ...item, checked: isChecked };
      }
      return item;
    });

    this.filterHistory.offset = 0;
    this.filterResult.emit(this.filterHistory);
    this.getHistory();
  }

  resetFilter() {
    this.filterHistory.start_date = moment().format('YYYY-MM-DD');
    this.filterHistory.end_date = moment().format('YYYY-MM-DD');
    this.filterHistory.transaction_type = DEFAULT_TRANSACTION_TYPES;
    this.selectedStatuses = [];

    this.filterHistory.offset = 0;
    this.filterResult.emit(this.filterHistory);
    this.getHistory();

    this.status.forEach((item) => {
      item.checked = false;
    });
  }

  openDatepicker() {
    this.pickerDirective.open();
  }

  onChangeDate(e: DateRangeChangeEvent | null) {
    if (!e || !e.startDate || !e.endDate) return;

    this.filterHistory.start_date = e.startDate.format('YYYY-MM-DD') + ' 00:00:00';
    this.filterHistory.end_date = e.endDate.format('YYYY-MM-DD') + ' 23:59:59';

    this.filterHistory.offset = 0;
    this.filterResult.emit(this.filterHistory);
    this.getHistory();
  }

  salinResi(v: string): void {
    navigator.clipboard
      .writeText(v)
      .then(() => {
        this.toastService.show(
          null,
          'Receipt Number successfully copied',
          'success',
          {
            classname: 'bg-success',
            delay: 3000,
          }
        );
      })
      .catch((e) => console.log(e));
  }

  getHistory(isScroll = false): void {
    this.balanceService
      .balanceHistory(this.filterHistory)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (res) => {
          if (isScroll) {
            this.HistoryList = [...this.HistoryList, ...res.data];
          } else {
            this.HistoryList = res.data;
          }
        },
      );
  }

  onScroll(event: Event) {
    const target = event.target as HTMLElement;
    if (target.offsetHeight + target.scrollTop + 1 >= target.scrollHeight) {
      this.filterHistory.offset += this.filterHistory.limit;
      this.getHistory(true);
    }
  }

  handleDownload(): void {
    const modal = this.modalService.open(ModalDlRincianSaldo, {
      backdrop: 'static',
      centered: true,
      size: 'lg',
    });

    modal.componentInstance.data = this.balance;
  }

  typePayment(v: string) {
    switch (v) {
      case 'BANK TRANSFER':
        return 'Non-COD Orders';
      case '':
        return 'Topup Balance';
      case 'bank_transfer':
        return 'Payment Order';
      case 'qrisly':
        return 'QRISLY';
      case 'Adjustment':
        return 'Adjustment';
      case 'order_damaged':
        return 'Damaged Package Compensation';
      case 'order_missing':
        return 'Lost Package Compensation'
      default:
        return 'COD Orders';
    }
  }

  typeTransaction(v: string) {
    const types: { [key: string]: string } = {
      withdrawal: 'Withdrawal',
      order_shipping: 'Shipping Cost',
      order_returned: 'Return',
      order_received: 'Received',
      order_cancel: 'Canceled',
      top_up: 'Topup Balance',
      insurance_fee: 'Insurance',
      bank_transfer: 'Bank Transfer',
      qris: 'QRIS',
      qrisly: 'QRISLY',
      gosend_price_difference: 'Price Difference',
      order_damaged: 'Damaged Package Compensation',
      order_missing: 'Lost Package Compensation'
    };
    return types[v] || '';
  }

  classTransaction(v: string) {
    const classes: { [key: string]: string } = {
      order_returned: 'text-danger',
      order_received: 'text-success',
      insurance_fee: 'text-danger',
    };
    return classes[v] || 'text-secondary';
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
